import type { TaskInterval } from '@cognite/apm-client';
import dayjs from 'dayjs';
import { Frequency, RRule, Weekday } from 'rrule';

import type { TimeStrings } from './types';

// Helper to parse "1MO", "-1FR", etc. into RRule weekday instances
export const parseByDayToByWeekday = (byDay: string[]): Weekday[] => {
  const weekdays: (Weekday | undefined)[] = byDay.map((entry) => {
    const match = entry.match(/^(-?\d)?(MO|TU|WE|TH|FR|SA|SU)$/);
    if (!match) return undefined;

    const [, nth, day] = match;
    const weekday = RRule[day as keyof typeof RRule];
    if (!(weekday instanceof Weekday)) return undefined;

    return nth ? weekday.nth(Number(nth)) : weekday;
  });
  const validweekdays: Weekday[] = weekdays.filter(
    (weekday): weekday is Weekday => weekday !== undefined
  );
  return validweekdays;
};

export const scheduleToRRule = (schedule: TaskInterval): RRule | undefined => {
  const scheduleFrequency: Frequency | undefined = (() => {
    switch (schedule.freq) {
      case 'DAILY':
        return Frequency.DAILY;
      case 'WEEKLY':
        return Frequency.WEEKLY;
      case 'MONTHLY':
        return Frequency.MONTHLY;
      case 'YEARLY':
        return Frequency.YEARLY;
      case 'HOURLY':
        return Frequency.HOURLY;
      default:
        return undefined;
    }
  })();

  if (
    scheduleFrequency !== undefined &&
    schedule.startTime &&
    schedule.interval
  ) {
    const rule = new RRule({
      freq: scheduleFrequency,
      dtstart: dayjs(schedule.startTime).toDate(),
      interval: Number(schedule.interval),
      bymonth: schedule.byMonth?.map((month) => Number(month)),
      byweekday: parseByDayToByWeekday(schedule.byDay || []),
      tzid: schedule.timezone,
    });
    return rule;
  }
  return undefined;
};

/**
 * Returns the next occurrence of any schedule from the provided list.
 * It calculates the next occurrence for each schedule and returns the earliest one.
 * If no valid schedules are provided, it returns null.
 *
 * @param {TaskInterval[]} schedules - An array of TaskInterval objects representing the schedules.
 * @returns {Date | null} - The earliest next occurrence date from the schedules, or null if no valid schedules are provided.
 */
export const schedulesToNextOccurrence = (
  schedules: TaskInterval[]
): Date | null => {
  const rrules = schedules
    .map((schedule) => scheduleToRRule(schedule))
    .filter((rule) => rule !== undefined);

  const nextOccurences = rrules?.map((rule) => {
    const next = rule?.after(new Date(), true);
    return next;
  });

  // Find the earliest next occurrence from all schedules
  const firstNextOccurence = nextOccurences?.reduce((acc, curr) => {
    if (curr && (!acc || curr < acc)) {
      return curr;
    }
    return acc;
  }, null);

  return firstNextOccurence;
};

// Helper function for singular/plural formatting
const formatDiff = (diff: number, singular: string, plural: string): string => {
  return diff === 1 ? `1 ${singular}` : `${diff} ${plural}`;
};

/**
 * Returns a human-readable string representing the time until the next occurrence of a schedule.
 * The function will return the largest unit of time (days, weeks, months, or years) until the next occurrence.
 * If the next occurrence is today, it returns "today".
 *
 * @param {Date} nextOccurence - The date of the next occurrence.
 * @param {Object} [timeStrings] - An object to customize the output strings for different time units.
 * @returns {string | undefined} - A human-readable string indicating the time until the next occurrence, or undefined if the input is invalid.
 */
export const readableTimeUntilNextOccurence = (
  nextOccurence: Date,
  timeStrings: TimeStrings = {
    today: 'today',
    day: 'day',
    days: 'days',
    week: 'week',
    weeks: 'weeks',
    month: 'month',
    months: 'months',
    year: 'year',
    years: 'years',
  }
): string | undefined => {
  const today = dayjs().startOf('day');
  const now = dayjs();

  if (dayjs(nextOccurence).isBefore(now)) {
    return undefined; // Next occurrence is in the past
  }
  if (dayjs(nextOccurence).isToday()) {
    return timeStrings.today;
  }
  if (dayjs(nextOccurence).isAfter(today.add(1, 'year'))) {
    const yearsDiff = Math.floor(dayjs(nextOccurence).diff(today, 'year'));
    return formatDiff(yearsDiff, timeStrings.year, timeStrings.years);
  }
  if (dayjs(nextOccurence).isAfter(today.add(1, 'month'))) {
    const monthsDiff = Math.floor(dayjs(nextOccurence).diff(today, 'month'));
    return formatDiff(monthsDiff, timeStrings.month, timeStrings.months);
  }
  if (dayjs(nextOccurence).isAfter(today.add(1, 'week'))) {
    const weeksDiff = Math.floor(dayjs(nextOccurence).diff(today, 'week'));
    return formatDiff(weeksDiff, timeStrings.week, timeStrings.weeks);
  }
  if (dayjs(nextOccurence).isAfter(today.add(1, 'day'))) {
    const daysDiff = Math.floor(dayjs(nextOccurence).diff(today, 'day'));
    return formatDiff(daysDiff, timeStrings.day, timeStrings.days);
  }

  return undefined;
};
